import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getOverlayGeometry, getOverlayInsetPct, getPageMetrics } from './atelierLayoutGeometry';
import { makeSlotHandlers } from './atelierSlotInteractions';
import { slotAcceptsItem } from './atelierLayouts';
import { checkSlotImageFit } from './photoQuality';
import { slotCroppedFraction } from './slotCrop';
import SlotFramingLayer from './SlotFramingLayer';
import { normalizeFraming, CROP_WARNING_THRESHOLD } from './framing';
import { isLeftPage } from '../../../utils/pageParity';
import AtelierPhotoLightbox from './AtelierPhotoLightbox';
import AtelierTextEditor from './AtelierTextEditor';
import { defaultRoleForSlot, slotBoxMm } from './textQuality';
import PhotoFitBadge from '../../common/PhotoFitBadge';

// Page de texte seul : au-dela, le texte devient trop petit pour etre lu a l'impression (correctif 7).
const TEXT_ONLY_MAX_CHARS = 600;

// Incrustation directe sur la page centrale ("tester un truc" — variante
// demandee en plus du panneau "Mise en page" existant, pas a sa place) :
// mêmes emplacements, même etat (draftSlotItemIds), même glue de
// glisser-deposer (atelierSlotInteractions) que le panneau de droite —
// seulement positionnes par-dessus le vrai rendu (l'iframe de la page) pour
// pouvoir deposer directement au bon endroit visuel plutot que sur une liste
// abstraite a cote. Approximatif par construction (voir
// atelierLayoutGeometry.js) : le panneau de droite reste utilisable en
// parallele, cette incrustation n'est qu'un raccourci visuel.
//
// L'iframe en dessous a pointer-events:none (voir BookAtelierLuxe.css) : les
// clics/drops passent donc naturellement a travers jusqu'a ces emplacements,
// sans qu'il soit necessaire de communiquer avec le contenu de l'iframe.
//
// Libelles d'un emplacement VIDE (refonte 2026-09-26, §6 : "supprimer les
// textes d'aide permanents... dans une zone vide, utiliser simplement
// 'Ajouter une photo'"). Le detail du geste (glisser OU choisir) ne
// disparait pas : il vit desormais dans le `title` (infobulle au survol,
// voir plus bas), pas dans un texte affiche en permanence.
// Type PROPRE a l'echange de deux emplacements de la page (distinct de
// l'application/json d'une photo glissee depuis la bibliotheque).
export const SLOT_DRAG_TYPE = 'application/x-bookipix-slot';

function XIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
      <path d="M5 5L19 19M19 5L5 19" />
    </svg>
  );
}

function CaptionIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 7h16M9 7v12" />
      <path d="M4 17h5" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1.5 12S5 5 12 5s10.5 7 10.5 7-3.5 7-10.5 7S1.5 12 1.5 12Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

// Meme icone que CoverPhotoOverlay (AtelierBookView.js) — un seul et meme
// geste "changer la photo" dans tout l'atelier, jamais deux pictogrammes
// differents pour la meme action selon qu'on est sur une page interieure ou
// la couverture.
function ChangeIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="5" width="14" height="14" rx="2" />
      <path d="M8 12l2.5 2.5L15 10" />
      <path d="M21 8v6a2 2 0 0 1-2 2h-2" />
    </svg>
  );
}

function AtelierPageOverlay({
  slug, slotTypes, slotItems, onAssignSlot, onRemoveSlot,
  selectedSidebarItem, printFormat,
  // Cadrage PAR EMPLACEMENT (phase 4a, voir framing.js) : identifiants stables des
  // emplacements, cadrages { [slotId]: {fit, focusX, focusY, zoom} }, et le
  // cadrage selectionne (barre flottante). Un clic sur une photo la SELECTIONNE ;
  // plus aucune fenetre ne s'ouvre.
  pageIndex, slotIds, framing, selectedSlotIndex, onSelectSlot, onFramingChange,
  // Phase 4b : echanger deux emplacements en glissant l'un sur l'autre, petit
  // selecteur de photos ouvert SUR l'emplacement (plus de grande fenetre), et
  // texte en cours d'edition porte par l'etat d'interface (le panneau de gauche
  // s'y adapte). Absents : comportement d'avant.
  onSwapSlots, pickerSlotIndex, pickerPhotos, onPickPhoto, onPickerUpload, pickerUploading, pickerError, onClosePicker,
  editingIndex: editingIndexProp, onEditingChange,
  // Legende par photo ({ [itemId]: texte }) + son enregistrement. Absents =
  // aucun bouton legende affiche : aucun appelant existant n est casse.
  photoCaptions, onSaveCaption,
  // Edition du texte directement sur la page (§1). Absent = comportement
  // d'avant (clic sur un texte = retrait a deux temps), donc aucun appelant
  // existant n'est casse s'il ne fournit pas ces props.
  onSaveText, onCreateText, textRoles, textStyles,
  // Emplacement PHOTO VIDE, au clic (retour utilisateur, 2026-09-25) : ouvre
  // le choix entre une photo deja importee et un nouvel import depuis le
  // disque (voir AtelierPhotoPickerModal.js, monte par l'appelant). Absent =
  // comportement d'avant (rien ne se passe sur un emplacement vide sans
  // selection prealable dans "Mes photos") — aucun appelant existant cassé.
  onOpenPhotoPicker,
  // Ouvre le tiroir "Mise en page" (retour utilisateur, 2026-10-04 : meme
  // menu a deux lignes que la couverture — "changer une mise en forme" au
  // dessus, "choisir une photo" en dessous). Facultatif : sans lui, le
  // bouton ne s'affiche simplement pas.
  onOpenLayoutDrawer,
  // (surplus colle) => ouvre une page de texte sur la page suivante ; absent quand il n'y en a pas.
  onContinueNextPage
}) {
  const { t } = useTranslation('atelier');
  // Index de l'emplacement en cours d'edition, et largeur REELLE de
  // l'incrustation en pixels : celle-ci sert a convertir les points
  // typographiques en pixels ecran a la bonne echelle (WYSIWYG, §1).
  const [localEditingIndex, setLocalEditingIndex] = useState(null);
  const editingIndex = editingIndexProp !== undefined ? editingIndexProp : localEditingIndex;
  const setEditingIndex = onEditingChange || setLocalEditingIndex;
  // Emplacement dont on edite la LEGENDE (distinct de editingIndex, qui edite
  // le texte d un emplacement texte) et sa valeur en cours de frappe.
  const [captionIndex, setCaptionIndex] = useState(null);
  const [captionDraft, setCaptionDraft] = useState('');
  // Couleur de la legende en cours d'edition. DEUX valeurs seulement : une
  // legende blanche devient illisible sur une photo tres claire, et c'est le
  // seul vrai choix a offrir (decision produit 2026-09-15). Pas de palette.
  const [captionColor, setCaptionColor] = useState('blanc');
  const [overlayWidthPx, setOverlayWidthPx] = useState(0);
  const overlayRef = useRef(null);
  // Marges reelles de CE format (et non des valeurs A4 figees) : elles
  // positionnent l'incrustation ET servent de reference d'echelle a
  // l'edition en ligne.
  const overlayInset = getOverlayInsetPct(printFormat);
  const pageMetrics = getPageMetrics(printFormat);

  useEffect(() => {
    const node = overlayRef.current;
    if (!node) return undefined;
    const measure = () => setOverlayWidthPx(node.getBoundingClientRect().width);
    measure();
    // La page se redimensionne avec la fenetre : sans cet observateur, la
    // typographie de l'editeur resterait a l'echelle du premier rendu et ne
    // correspondrait plus a ce qui est affiche dessous.
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Retrait a deux temps (retour utilisateur : jamais de retrait direct au
  // clic, il faut une confirmation) — meme mecanisme que le panneau "Mise
  // en page" (AtelierLayoutPanel.js), voir atelierSlotInteractions.js pour
  // la logique partagee. Reinitialise par le `key` pose par l'appelant
  // (BookAtelierLuxe.js) a chaque changement de page/cote, pas par un effet
  // qui observerait slotItems (nouvelle reference a chaque rendu du parent).
  const [pendingRemoveIndex, setPendingRemoveIndex] = useState(null);
  // Apercu plein ecran d'une photo a sa vraie resolution (retour
  // utilisateur, 2026-09-11) — voir AtelierPhotoLightbox.js.
  const [viewingUrl, setViewingUrl] = useState(null);
  // Emplacement SURVOLE PENDANT UN GLISSEMENT (refonte 2026-09-26, §1 :
  // "les pointillés doivent être un état d'interaction, pas un élément
  // permanent"). Deliberement local a CET emplacement (dragenter/dragleave,
  // pas un etat global "un glissement est en cours quelque part") : c'est
  // la ou la photo tomberait maintenant qui doit s'illuminer, pas tous les
  // emplacements a la fois. `:hover` CSS aurait ete plus simple, mais ne
  // convient pas ici : le survol au clavier ou en glisser-deposer natif
  // (HTML5 drag) ne declenche pas de facon fiable les evenements de souris
  // dont depend `:hover` — dragenter/dragleave sont l API prevue pour ca.
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // Retour utilisateur (2026-09-10) : "confirmer le retrait" restait affiche
  // si on cliquait ailleurs QUE sur un autre emplacement (ex. la barre
  // laterale, le header, en dehors meme du livre) — seul un clic sur un
  // AUTRE emplacement l'annulait (via onCancelRemove ci-dessous, dans
  // handleDrop/handleClick). Ecoute globale : tout clic dont la cible n'est
  // PAS a l'interieur d'un emplacement de CETTE incrustation annule la
  // confirmation en attente. Un clic a l'interieur d'un emplacement (y
  // compris celui en attente, pour confirmer) n'est jamais intercepte ici —
  // c'est deja gere par l'onClick du emplacement lui-meme, dans le meme tour
  // d'evenement.
  useEffect(() => {
    if (pendingRemoveIndex == null) return undefined;
    function handleOutsideClick(event) {
      if (!event.target.closest('.atelier-overlay-slot')) setPendingRemoveIndex(null);
    }
    document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  }, [pendingRemoveIndex]);

  const geometry = getOverlayGeometry(slug);
  if (!geometry) return null;

  const { handleDrop, handleClick } = makeSlotHandlers({
    onAssignSlot,
    onRemoveSlot,
    selectedSidebarItem,
    pendingRemoveIndex,
    onRequestRemove: setPendingRemoveIndex,
    onCancelRemove: () => setPendingRemoveIndex(null)
  });

  // Retrait explicite via la croix (retour utilisateur, 2026-09-11 :
  // "afficher une x pour supprimer") — meme etat/meme confirmation a deux
  // temps que le clic sur l'emplacement (pendingRemoveIndex), mais
  // declenche par un bouton dedie plutot que par un clic ambigu n'importe
  // ou sur la photo (qui reste par ailleurs possible, comportement
  // inchange, voir handleClick ci-dessus/atelierSlotInteractions.js).
  function handleRequestRemove(event, index) {
    event.stopPropagation();
    // Retrait IMMEDIAT : une confirmation a deux clics est remplacee par le
    // message flottant "Annuler" (voir handleRemoveSlot).
    onRemoveSlot(index);
    setPendingRemoveIndex(null);
  }

  return (
    <>
      <div
        ref={overlayRef}
        className="atelier-page-overlay"
        style={{
          top: `${overlayInset.top}%`,
          left: `${overlayInset.left}%`,
          right: `${overlayInset.right}%`,
          bottom: `${overlayInset.bottom}%`
        }}
      >
        {geometry.map((rect, index) => {
          const slotType = slotTypes[index];
          const item = slotItems[index] || null;
          const rejects = Boolean(selectedSidebarItem && !slotAcceptsItem(slotType, selectedSidebarItem));
          const isPending = pendingRemoveIndex === index;
          // Controle qualite EN TEMPS REEL (cahier des charges v2, §1/§4.1) :
          // calcule localement, aucun appel reseau (voir photoQuality.js).
          // Le badge n'apparait QUE pour 'limite'/'insuffisant' — une photo
          // correcte ne declenche aucun avertissement visible (critere
          // d'acceptation n°1) et le mot "DPI" n'est jamais affiche.
          const slotId = slotIds ? slotIds[index] : null;
          const slotFraming = normalizeFraming(slotId && framing ? framing[slotId] : null);
          const fit = item && slotType === 'photo'
            ? checkSlotImageFit({
              item,
              layoutSlug: slug,
              slotIndex: index,
              printFormat,
              zoom: slotFraming.zoom,
              // Sans le mode, une photo affichee ENTIERE etait jugee comme si
              // elle etait rognee : badge de flou injustifie sur un reglage
              // que l'utilisateur venait de choisir.
              fitMode: slotFraming.fit
            })
            : null;
          // Part de la photo COUPEE par le remplissage : au-dela de 30 %, un petit
          // point orange (jamais une fenetre) l'indique, avec le detail en infobulle.
          const croppedPct = item && slotType === 'photo'
            ? slotCroppedFraction({ item, layoutSlug: slug, slotIndex: index, printFormat, framing: slotFraming })
            : 0;
          const isFramingSelected = Boolean(item) && slotType === 'photo' && selectedSlotIndex === index;
          const onlyPickerOpen = pickerSlotIndex === index;
          const occurrence = slotItems.slice(0, index).filter((other) => other && item && other.id === item.id).length;
          return (
            <div
              key={index}
              className={`atelier-overlay-slot ${item ? 'is-filled' : ''} ${isFramingSelected ? 'is-framing-selected' : ''} ${onlyPickerOpen ? 'is-pick-target' : ''} ${rejects ? 'is-rejecting' : ''} ${isPending ? 'is-pending-remove' : ''} ${dragOverIndex === index ? 'is-drag-target' : ''}`}
              style={{ top: `${rect.top}%`, left: `${rect.left}%`, width: `${rect.width}%`, height: `${rect.height}%` }}
              onDragOver={(event) => event.preventDefault()}
              onDragEnter={() => setDragOverIndex(index)}
              // `relatedTarget` : un dragleave se declenche aussi en passant
              // d'un enfant a un autre DANS le meme emplacement (l'etiquette,
              // le badge qualite...) — sans ce filtre, les pointillés
              // clignotaient tout du long du survol au lieu de rester fixes.
              onDragLeave={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) setDragOverIndex(null);
              }}
              // Echange par glisser-deposer : on saisit un emplacement REMPLI (hors
              // cadrage en cours, ou le glisser deplace la photo dans son cadre) et on
              // le depose sur un autre emplacement de la meme page.
              draggable={Boolean(item) && !isFramingSelected && !isPending && editingIndex !== index && !onlyPickerOpen}
              onDragStart={(event) => {
                if (!item) return;
                event.dataTransfer.effectAllowed = 'move';
                event.dataTransfer.setData(SLOT_DRAG_TYPE, JSON.stringify({ pageIndex, slotIndex: index }));
              }}
              onDrop={(event) => {
                event.stopPropagation();
                setDragOverIndex(null);
                const rawSlot = event.dataTransfer.getData(SLOT_DRAG_TYPE);
                if (rawSlot) {
                  event.preventDefault();
                  try {
                    const source = JSON.parse(rawSlot);
                    if (onSwapSlots && source.pageIndex === pageIndex && source.slotIndex !== index) onSwapSlots(source.slotIndex, index);
                  } catch (_error) { /* charge etrangere : ignoree */ }
                  return;
                }
                handleDrop(event, index, slotType);
              }}
              onClick={(event) => {
                event.stopPropagation();
                // Un retrait ARME passe avant tout : c'est le second clic qui
                // le confirme. Sans cette priorite, le clic de confirmation
                // rouvrait l'editeur de texte et "Confirmer le retrait ?"
                // restait affiche sans jamais pouvoir aboutir (bug signale
                // 2026-09-11).
                if (isPending) {
                  handleClick(index, item, slotType);
                  return;
                }
                // Phase 4a : un clic sur une photo deja placee la SELECTIONNE (contour
                // + barre flottante pres d'elle) — plus de fenetre. Priorite a
                // l'assignation quand un element est selectionne dans "Mes souvenirs".
                if (!selectedSidebarItem && item && slotType === 'photo' && onSelectSlot) {
                  if (!isFramingSelected) onSelectSlot(index);
                  return;
                }
                // Emplacement PHOTO VIDE (retour utilisateur, 2026-09-25) :
                // ouvrir directement le choix entre une photo deja importee
                // et un nouvel import, plutot que de forcer un detour par
                // "Mes photos" avant de revenir cliquer ici. Meme logique de
                // priorite que ci-dessus : seulement si rien n'est deja
                // selectionne dans la colonne de gauche.
                if (!selectedSidebarItem && !item && slotType === 'photo' && onOpenPhotoPicker) {
                  setPendingRemoveIndex(null);
                  onOpenPhotoPicker(index);
                  return;
                }
                // Meme principe pour le TEXTE (cahier des charges
                // typographique §1) : un clic sur un texte deja place ouvre
                // l'edition EN PLACE, il n'arme pas un retrait — le retrait a
                // sa croix, comme pour les photos.
                if (!selectedSidebarItem && slotType !== 'photo' && (item ? onSaveText : onCreateText)) {
                  // Emplacement VIDE : on ouvre l'editeur pour ecrire
                  // directement, sans passer par "Mes souvenirs" (retour
                  // utilisateur : "il faut pouvoir ecrire directement dans
                  // les cases vierges ou bien glisser un souvenir").
                  // Un retrait arme sur un AUTRE emplacement n'a plus lieu
                  // d'etre si l'utilisateur part editer un texte.
                  setPendingRemoveIndex(null);
                  setEditingIndex(index);
                  return;
                }
                handleClick(index, item, slotType);
              }}
              // Correctif 5 : plus de bulle native (title) — elles debordaient de l'ecran. Au repos, seul le curseur
              // « main » indique qu'on peut saisir la photo ; l'aide vit dans la barre (info-bulle de premier usage).
              aria-label={item
                ? (isPending
                  ? t('pageOverlay.clickToConfirmRemove')
                  : (slotType === 'photo' ? t('pageOverlay.clickToAdjust') : (onSaveText ? t('pageOverlay.clickToEditText') : undefined)))
                : (t(`pageOverlay.slotHints.${slotType}`, { defaultValue: '' }) || undefined)}
            >
              {/* Emplacement photo VIDE : cadre pointille avec « + » et « Ajouter une photo » (correctif 4). Plus de
                  bouton « Changer de mise en page » repete sur chaque emplacement : l'action vit une seule fois dans
                  la barre de la page (AtelierPageActions). */}
              {!item && slotType === 'photo' ? (
                <span className="atelier-overlay-slot-add">
                  <b aria-hidden="true">+</b>
                  <span>{t('pageOverlay.slotLabels.photo')}</span>
                </span>
              ) : (
                !item && (
                  <span className="atelier-overlay-slot-add is-text">
                    <b aria-hidden="true">✎</b>
                    <span>{t('pageOverlay.writeHere')}</span>
                  </span>
                )
              )}
              {!isPending && (
                <span className="atelier-overlay-slot-quality">
                  <PhotoFitBadge fit={fit} size="sm" />
                </span>
              )}
              {croppedPct > CROP_WARNING_THRESHOLD && !isPending && (
                <span
                  className="atelier-overlay-slot-crop"
                  title={t('framing.cropWarning', { percent: Math.round(croppedPct * 100) })}
                  aria-label={t('framing.cropWarning', { percent: Math.round(croppedPct * 100) })}
                  role="img"
                />
              )}
              {isFramingSelected && onFramingChange && (
                <SlotFramingLayer
                  target={{ pageIndex, spread: slug === 'FULL_PHOTO_SPREAD' }}
                  item={item}
                  occurrence={occurrence}
                  imageRatio={item.metadata?.width && item.metadata?.height ? item.metadata.width / item.metadata.height : null}
                  framing={slotFraming}
                  onFramingChange={(patch, options) => onFramingChange(index, patch, options)}
                  onDeselect={() => onSelectSlot(null)}
                  onReplace={onOpenPhotoPicker ? () => { onSelectSlot(null); onOpenPhotoPicker(index); } : null}
                  onRemove={() => { onSelectSlot(null); onRemoveSlot(index); }}
                  spreadSide={slug === 'FULL_PHOTO_SPREAD' ? (isLeftPage(pageIndex) ? 'left' : 'right') : null}
                  printFormat={printFormat}
                />
              )}
              {item && isPending && <span className="atelier-overlay-slot-confirm">{t('pageOverlay.confirmRemove')}</span>}
              {item && !isPending && (
                <div className="atelier-overlay-slot-actions">
                  {slotType === 'photo' && onSaveCaption && (
                    <button
                      type="button"
                      className={`atelier-overlay-slot-icon-btn atelier-overlay-slot-caption-btn ${photoCaptions?.[item.id] ? 'is-set' : ''}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        setPendingRemoveIndex(null);
                        const existante = photoCaptions?.[item.id];
                        setCaptionDraft(typeof existante === 'string' ? existante : (existante?.texte || ''));
                        setCaptionColor(existante?.couleur === 'noir' ? 'noir' : 'blanc');
                        setCaptionIndex(index);
                      }}
                      title={photoCaptions?.[item.id] ? t('pageOverlay.editCaption') : t('pageOverlay.addCaption')}
                      aria-label={photoCaptions?.[item.id] ? t('pageOverlay.editCaption') : t('pageOverlay.addCaption')}
                    >
                      <CaptionIcon />
                    </button>
                  )}
                  {/* "Changer la photo" (retour utilisateur, 2026-09-29 :
                      "quand je clique sur une image j'accede uniquement a
                      'ajuster le cadrage'... il faut donner aussi la
                      possibilite de modifier l'image") — meme geste et meme
                      fenetre que pour un emplacement VIDE
                      (AtelierPhotoPickerModal via onOpenPhotoPicker), juste
                      declenche depuis un emplacement DEJA rempli. Reprend le
                      pattern deja existant sur la couverture (voir
                      CoverPhotoOverlay, AtelierBookView.js — meme retour
                      utilisateur, 2026-09-26), qui n'avait jamais ete
                      etendu aux pages interieures. */}
                  {slotType === 'photo' && onOpenPhotoPicker && (
                    <button
                      type="button"
                      className="atelier-overlay-slot-icon-btn atelier-overlay-slot-change-btn"
                      onClick={(event) => {
                        event.stopPropagation();
                        setPendingRemoveIndex(null);
                        onOpenPhotoPicker(index);
                      }}
                      title={t('pageOverlay.changePhotoTitle')}
                      aria-label={t('pageOverlay.changePhotoLabel')}
                    >
                      <ChangeIcon />
                    </button>
                  )}
                  {slotType === 'photo' && (
                    <button
                      type="button"
                      className="atelier-overlay-slot-icon-btn atelier-overlay-slot-view-btn"
                      onClick={(event) => {
                        event.stopPropagation();
                        // Voir le commentaire de AtelierSidebar : version
                        // d'ecran, pas l'original.
                        setViewingUrl(item.metadata?.previewUrl || item.url);
                      }}
                      title={t('pageOverlay.viewFullSize')}
                      aria-label={t('pageOverlay.viewFullSize')}
                    >
                      <EyeIcon />
                    </button>
                  )}
                  <button
                    type="button"
                    className="atelier-overlay-slot-icon-btn atelier-overlay-slot-remove-btn"
                    onClick={(event) => handleRequestRemove(event, index)}
                    title={t('pageOverlay.remove')}
                    aria-label={t('pageOverlay.remove')}
                  >
                    <XIcon />
                  </button>
                </div>
              )}
              {/* Saisie de la legende, posee au bas de l'emplacement — la ou
                  elle s'imprimera. Entree valide, Echap annule ; vider le
                  champ retire la legende. */}
              {item && captionIndex === index && (
                <form
                  className={`atelier-overlay-caption-form is-${captionColor}`}
                  onClick={(event) => event.stopPropagation()}
                  onSubmit={(event) => {
                    event.preventDefault();
                    onSaveCaption(item.id, captionDraft, captionColor);
                    setCaptionIndex(null);
                  }}
                >
                  {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
                  <input
                    type="text"
                    autoFocus
                    maxLength={140}
                    value={captionDraft}
                    placeholder={t('pageOverlay.captionPlaceholder')}
                    onChange={(event) => setCaptionDraft(event.target.value)}
                    onKeyDown={(event) => { if (event.key === 'Escape') { event.stopPropagation(); setCaptionIndex(null); } }}
                  />
                  {/* Blanc / noir. Les deux pastilles montrent le RESULTAT
                      (texte clair sur fond sombre, texte sombre sur fond
                      clair) plutot qu'un simple carre de couleur : c'est le
                      contraste qu'on choisit, pas la teinte. */}
                  <span className="atelier-overlay-caption-colors" role="group" aria-label={t('pageOverlay.captionColorGroupLabel')}>
                    {[
                      { id: 'blanc', libelle: t('pageOverlay.captionColorWhite') },
                      { id: 'noir', libelle: t('pageOverlay.captionColorBlack') }
                    ].map((choix) => (
                      <button
                        key={choix.id}
                        type="button"
                        className={`atelier-overlay-caption-color is-${choix.id} ${captionColor === choix.id ? 'is-active' : ''}`}
                        onClick={() => setCaptionColor(choix.id)}
                        title={choix.libelle}
                        aria-label={choix.libelle}
                        aria-pressed={captionColor === choix.id}
                      >
                        Aa
                      </button>
                    ))}
                  </span>
                  <button type="submit" className="atelier-overlay-caption-ok">{t('pageOverlay.ok')}</button>
                </form>
              )}
            </div>
          );
        })}

        {/* Editeur en ligne : monte DANS l'incrustation, donc positionne
            dans le meme repere en % que les emplacements — il se superpose
            exactement au texte qu'il remplace. */}
        {editingIndex != null && geometry[editingIndex] && (
          <AtelierTextEditor
            // Emplacement vide : un item "neuf" sans id — c'est onCreateText
            // qui le fera exister, et seulement si l'utilisateur ecrit
            // quelque chose.
            item={slotItems[editingIndex] || { id: null, text: '' }}
            rect={geometry[editingIndex]}
            role={textRoles?.[slotItems[editingIndex]?.id] || defaultRoleForSlot(slug, editingIndex)}
            styleOverrides={textStyles?.[slotItems[editingIndex]?.id] || {}}
            printFormat={printFormat}
            reference={{ referenceWidthPx: overlayWidthPx, referenceWidthMm: pageMetrics.contentWidthMm }}
            {...slotBoxMm(geometry[editingIndex], printFormat)}
            // Page de TEXTE SEUL (correctif 7) : compteur 600, centrage vertical, suite sur la page suivante.
            maxChars={slug === 'ONE_TESTIMONY' ? TEXT_ONLY_MAX_CHARS : null}
            verticalCenter={slug === 'ONE_TESTIMONY'}
            onContinueNextPage={slug === 'ONE_TESTIMONY' ? onContinueNextPage : null}
            onSave={(payload) => {
              const existing = slotItems[editingIndex];
              if (existing) onSaveText(existing.id, payload);
              else onCreateText(editingIndex, payload);
              setEditingIndex(null);
            }}
            onCancel={() => setEditingIndex(null)}
          />
        )}
      </div>
      <AtelierPhotoLightbox url={viewingUrl} onClose={() => setViewingUrl(null)} />
    </>
  );
}

export default AtelierPageOverlay;
